// ─────────────────────────────────────────────────────────────
// CloudflareSave — Save current project to Cloudflare R2
// Same as the normal Save but destination is R2 instead of OPFS.
// Uses RuntimeNotifier.progress() so the dialog looks identical
// to the Google/Dropbox backup dialog.
// ─────────────────────────────────────────────────────────────

import {DefaultObservableValue, Errors, RuntimeNotifier, RuntimeSignal, unitValue} from "@opendaw/lib-std"
import {ProjectSignals, ProjectStorage} from "@opendaw/studio-core"
import {CloudflareHandler} from "./CloudflareHandler"
import {CloudflareIndexManager} from "./CloudflareIndexManager"

export namespace CloudflareSave {

    export const save = async (projectUuid: Uint8Array): Promise<void> => {
        const handler = new CloudflareHandler()

        // Verify worker is reachable first
        try {
            await handler.alive()
        } catch {
            await RuntimeNotifier.info({
                headline: "Cloudflare Unavailable",
                message: "Could not reach the Cloudflare DAW worker. Check your connection and try again."
            })
            return
        }

        const progressValue = new DefaultObservableValue<unitValue>(0.0)
        const dialog = RuntimeNotifier.progress({
            headline: "Saving to Cloudflare",
            progress: progressValue
        })

        try {
            // ── Load project files from OPFS ──────────────────
            dialog.message = "Reading project..."
            progressValue.setValue(0.1)

            const projectData = await ProjectStorage.loadProject(projectUuid)
            const metaData = await ProjectStorage.loadMeta(projectUuid)
            const coverData = await ProjectStorage.loadCover(projectUuid)

            const {UUID} = await import("@opendaw/lib-std")
            const uuidStr = UUID.toString(projectUuid)
            const folder = `projects/${uuidStr}`

            // ── Upload project files ──────────────────────────
            dialog.message = "Uploading project file..."
            progressValue.setValue(0.3)
            await handler.upload(`${folder}/project.od`, projectData)

            dialog.message = "Uploading project info..."
            progressValue.setValue(0.6)
            await handler.upload(`${folder}/meta.json`, metaData)

            coverData.ifSome(async cover => {
                await handler.upload(`${folder}/image.bin`, cover)
            })

            // ── Update the index ──────────────────────────────
            dialog.message = "Updating catalog..."
            progressValue.setValue(0.85)
            await CloudflareIndexManager.updateProjectInIndex(handler, projectUuid)

            progressValue.setValue(1.0)
            dialog.message = "Done"

            RuntimeSignal.dispatch(ProjectSignals.StorageUpdated)

            await RuntimeNotifier.info({
                headline: "Saved to Cloudflare",
                message: "Your project has been saved to Cloudflare successfully."
            })
        } catch (reason: unknown) {
            if (Errors.isAbort(reason)) return
            console.warn("[CloudflareSave]", reason)
            await RuntimeNotifier.info({
                headline: "Save to Cloudflare Failed",
                message: String(reason)
            })
        } finally {
            progressValue.terminate()
            dialog.terminate()
        }
    }
}
